import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/SEO";
import { Player } from "@lottiefiles/react-lottie-player";

const MobileApps = () => {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible((prev) => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting,
          }));
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll("[data-animate]");
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      title: "React Native Development",
      description:
        "Building cross-platform mobile applications with React Native, enabling code reuse across iOS and Android while maintaining native performance.",
      icon: (
        <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      title: "Flutter Development",
      description:
        "Creating beautiful, fast mobile apps with Flutter's single codebase, delivering consistent experiences across iOS and Android platforms.",
      icon: (
        <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
        </svg>
      ),
    },
    {
      title: "Cross-Platform Solutions",
      description:
        "Developing apps that work seamlessly on both iOS and Android, reducing development time and costs while maximizing reach.",
      icon: (
        <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      title: "Native Performance",
      description:
        "Ensuring apps perform at native speeds with smooth animations, fast load times, and optimal resource utilization.",
      icon: (
        <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
    {
      title: "Seamless User Experience",
      description:
        "Designing intuitive interfaces and smooth interactions that provide exceptional user experiences across all devices.",
      icon: (
        <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: "iOS & Android Compatibility",
      description:
        "Ensuring full compatibility with both iOS and Android platforms, following platform guidelines and best practices.",
      icon: (
        <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
  ];

  const benefits = [
    "React Native and Flutter expertise",
    "Cross-platform development capabilities",
    "Native performance optimization",
    "Seamless user experiences across devices",
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      <SEO title="Mobile App Development" description="Creating cross-platform mobile applications with React Native and Flutter, delivering native performance." />
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#141414] via-[#121212] to-yellow-50/30">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-64 h-64 bg-[rgb(249,193,37)]/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-200/20 rounded-full blur-3xl animate-pulse animation-delay-2000"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 className="text-2xl md:text-3xl font-bold text-white leading-tight">
                Creating Cross-Platform{" "}
                <span className="text-[rgb(249,193,37)]">Mobile Applications</span>
              </h1>
              <p className="text-base md:text-lg text-[#9a9a9a] leading-relaxed">
                Creating cross-platform mobile applications with React Native and Flutter, delivering native performance and seamless user experiences across iOS and Android devices.
              </p>
            </div>

            <div className="relative">
              <div className="w-full h-96 flex items-center justify-center">
                <Player
                  autoplay
                  loop
                  src="https://lottie.host/08dbc9d3-009d-475d-b673-90cdef7912e3/8XZ49hOjrM.json"
                  style={{ height: "100%", width: "100%" }}
                  className="lottie-animation"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        id="what-we-offer"
        data-animate
        className="py-32 bg-[#0a0a0a] relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 25% 25%, rgb(249,193,37) 2px, transparent 2px),
                               radial-gradient(circle at 75% 75%, #000 1px, transparent 1px)`,
              backgroundSize: "50px 50px",
            }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div
            className={`text-center mb-20 ${
              isVisible["what-we-offer"] ? "animate-fade-in-up" : "opacity-0"
            }`}
          >
            <h2 className="text-2xl lg:text-4xl font-black text-white mb-6">
              What We <span className="text-[rgb(249,193,37)]">Offer?</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className={`service-card-interactive group ${
                  isVisible["what-we-offer"]
                    ? "animate-slide-in-up"
                    : "opacity-0"
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative bg-gradient-to-br from-[#171717] to-[#101010] rounded-3xl p-8 shadow-xl border-2 border-[#2a2a2a] h-full transition-all duration-500 hover:border-[rgb(249,193,37)] hover:shadow-2xl hover:scale-105 hover:-translate-y-2">
                  <div className="w-20 h-20 bg-gradient-to-br from-[rgb(249,193,37)] to-[rgb(249,193,37)] rounded-2xl flex items-center justify-center text-[#101010] mb-6 transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-500 shadow-lg">
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-black text-white mb-4 group-hover:text-[rgb(249,193,37)] transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-[#9a9a9a] leading-relaxed whitespace-pre-line">
                    {service.description}
                  </p>
                  <div className="absolute inset-0 bg-gradient-to-br from-[rgb(249,193,37)]/5 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="why-choose-us"
        data-animate
        className="py-32 bg-gradient-to-br from-[rgb(249,193,37)] via-yellow-400 to-[rgb(249,193,37)] relative overflow-hidden"
      >
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-64 h-64 bg-[#202020]/80 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#1a1a1a]/70 rounded-full blur-3xl animate-pulse animation-delay-2000"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div
            className={`text-center mb-20 ${
              isVisible["why-choose-us"] ? "animate-fade-in-up" : "opacity-0"
            }`}
          >
            <h2 className="text-2xl lg:text-4xl font-black text-[#101010] mb-6">
              Why Choose <span className="text-white">Us?</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className={`bg-[#0a0a0a]/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl transform transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:rotate-1 ${
                  isVisible["why-choose-us"]
                    ? "animate-slide-in-up"
                    : "opacity-0"
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[rgb(249,193,37)] rounded-full flex items-center justify-center flex-shrink-0 mr-4 transform hover:rotate-180 transition-transform duration-300">
                    <svg
                      className="w-5 h-5 text-[#101010]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="text-white font-semibold text-lg leading-relaxed">
                    {benefit}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div
            className={`text-center mt-16 ${
              isVisible["why-choose-us"]
                ? "animate-fade-in-up animation-delay-600"
                : "opacity-0"
            }`}
          >
            <p className="text-xl md:text-2xl text-[#101010] font-semibold mb-4 leading-relaxed">
              Let us help you create powerful mobile apps that deliver exceptional user experiences
            </p>
            <p className="text-lg text-white font-bold">
              Contact us today to get started!
            </p>
          </div>
        </div>
      </section>

      <section
        id="cta-section"
        data-animate
        className="py-32 bg-gradient-to-br from-[#141414] via-[#121212] to-yellow-50/30 relative overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div
            className={`${
              isVisible["cta-section"] ? "animate-fade-in-up" : "opacity-0"
            }`}
          >
            <Link
              to="/contact"
              className="inline-block group relative px-12 py-6 bg-gradient-to-r from-[rgb(249,193,37)] to-[rgb(249,193,37)] text-[#101010] font-bold text-xl rounded-full overflow-hidden transition-all duration-500 hover:scale-110 hover:shadow-2xl hover:shadow-[rgb(249,193,37)]/50"
            >
              <span className="relative z-10 flex items-center">
                Get Started
                <svg
                  className="ml-2 w-6 h-6 transform group-hover:translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-[rgb(249,193,37)] to-[rgb(249,193,37)] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MobileApps;


